import { useEffect, useState } from 'react';
import api from '../api';

// The measuring tool on the floor (designed with the director 22 Sep and
// 7 Oct 2026). Shared by the Record button and the Today card: what I have
// been given to measure today, and the recording in progress on this phone.
//
// The recording lives on the phone first: every tap is saved here the moment
// it is made, then sent to the server when there is signal. A locked phone, a
// closed tab or a dead spot in the hall loses nothing. A recording finished
// with no signal waits in the outbox and goes by itself when the signal is back
// (3 Nov 2026: "never let a patchy factory signal decide whether a measurement
// exists"), and the list is kept here too, so it opens in a dead spot.

const ACTIVE_KEY = 'mouldos.measuring.active';
const MINE_KEY = 'mouldos.measuring.mine';
const DEFS_KEY = 'mouldos.measuring.defs';
const OUTBOX_KEY = 'mouldos.measuring.outbox';

const read = (k, fallback) => { try { return JSON.parse(localStorage.getItem(k)) ?? fallback; } catch { return fallback; } };
const write = (k, v) => { try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, JSON.stringify(v)); } catch { /* private window: kept in memory only */ } };
const todayHere = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Ljubljana' });

const cachedMine = () => { const m = read(MINE_KEY, null); return m && m.day === todayHere() ? m : null; };
let state = { tasks: (cachedMine() || {}).tasks || [], running: (cachedMine() || {}).running || [], loaded: false, day: null, defs: read(DEFS_KEY, null), outbox: read(OUTBOX_KEY, []) };
const subs = new Set();
const emit = () => subs.forEach(f => f({ ...state }));

export async function refreshMine() {
  try {
    const r = await api.get('/api/v1/measuring/mine');
    state = { ...state, tasks: r.data.tasks || [], running: r.data.running || [], day: r.data.day, loaded: true };
    write(MINE_KEY, { day: r.data.day, tasks: state.tasks, running: state.running });
  } catch {
    // No signal: what was last read today stands.
    const c = cachedMine();
    state = { ...state, tasks: c ? c.tasks : state.tasks, running: c ? c.running : state.running, loaded: true };
  }
  emit();
}

export async function loadDefinitions() {
  if (state.defs) {
    // Kept for a dead spot, and refreshed behind it.
    api.get('/api/v1/measuring/definitions').then(r => { state = { ...state, defs: r.data.definitions }; write(DEFS_KEY, r.data.definitions); emit(); }).catch(() => {});
    return state.defs;
  }
  try {
    const r = await api.get('/api/v1/measuring/definitions');
    state = { ...state, defs: r.data.definitions };
    write(DEFS_KEY, r.data.definitions);
    emit();
  } catch { /* the recorder falls back to the step names */ }
  return state.defs;
}

export function useMyMeasuring() {
  const [s, set] = useState({ ...state });
  useEffect(() => { subs.add(set); set({ ...state }); return () => { subs.delete(set); }; }, []);
  return s;
}

// Open the recorder from anywhere, at a measurement or at the list.
export function openRecorder(taskId) {
  window.dispatchEvent(new CustomEvent('mouldos:recorder', { detail: { taskId: taskId || null } }));
}

export function loadActive() {
  try { return JSON.parse(localStorage.getItem(ACTIVE_KEY)) || null; } catch { return null; }
}
export function saveActive(a) {
  try { if (a) localStorage.setItem(ACTIVE_KEY, JSON.stringify(a)); else localStorage.removeItem(ACTIVE_KEY); } catch { /* private window: kept in memory only */ }
  emit();
}

// Send the taps so far; create the recording on the server first if it is
// not there yet. Never throws: unsent taps stay on the phone (synced: false).
export async function syncActive(a) {
  if (!a) return a;
  try {
    let id = a.id;
    if (!id) {
      const r = await api.post('/api/v1/measuring/measurements', { task_id: a.taskId, started_at: a.startedAt, marks: a.marks });
      id = r.data.measurement.id;
    }
    await api.put(`/api/v1/measuring/measurements/${id}`, { marks: a.marks });
    return { ...a, id, synced: true };
  } catch {
    return { ...a, synced: false };
  }
}

// ── the outbox ──────────────────────────────────────────────────────────────
//   { taskId, id, startedAt, title, kind, body, error }
const setOutbox = (list) => { state = { ...state, outbox: list }; write(OUTBOX_KEY, list.length ? list : null); emit(); };

// Send one finished recording: { ok, measurement } when it went, { offline } when
// there is no signal, { refused } when the server said no, with its reason.
export async function sendFinished(item) {
  try {
    let id = item.id;
    if (!id) {
      const s = await api.post('/api/v1/measuring/measurements', { task_id: item.taskId, started_at: item.startedAt, marks: item.body.marks || [] });
      id = s.data.measurement.id;
    }
    const r = await api.post(`/api/v1/measuring/measurements/${id}/finish`, item.body);
    return { ok: true, measurement: r.data.measurement };
  } catch (err) {
    if (!err.response) return { offline: true };
    // Sent earlier and the answer was lost: it is in.
    if (err.response.status === 409 && /already/i.test(err.response.data?.error || '')) return { ok: true, measurement: null };
    return { refused: err.response.data?.error || 'The app could not take this recording.' };
  }
}
export function queueFinished(item) { setOutbox([...state.outbox.filter(x => x.taskId !== item.taskId), { ...item, error: null }]); }
export function dropQueued(taskId) { setOutbox(state.outbox.filter(x => x.taskId !== taskId)); }

let flushing = false;
export async function flushOutbox() {
  if (flushing || !state.outbox.length) return;
  flushing = true;
  try {
    let list = state.outbox;
    let any = false;
    for (const item of list) {
      if (item.error) continue;
      const r = await sendFinished(item);
      if (r.offline) break;
      any = true;
      list = r.ok ? list.filter(x => x !== item) : list.map(x => (x === item ? { ...x, error: r.refused } : x));
    }
    setOutbox(list);
    if (any) refreshMine();
  } finally {
    flushing = false;
  }
}

// A clock left running: twice what it should take, and never soon. The same
// rule as the server's notification (utils/measureResult.js).
export function stillOnItAfter(kind, assumed) {
  const a = assumed || {};
  if (kind === 'cycle') return null;
  if (kind === 'mould_change') return Math.max(120, 2 * ((Number(a.off_min) || 0) + (Number(a.on_min) || 0)));
  if (kind === 'warmup') return Math.max(60, 2 * (Number(a.warmup_min) || 30));
  if (kind === 'cleaning') return Math.max(60, 2 * (Number(a.cleaning_min) || 20));
  if (kind === 'purge') return 120;
  if (kind === 'attention') return 180;
  return 600;
}

// Packing: the taps grouped into pallets, as the server groups them. The box
// that fills a pallet closes it; "pallet finished" (P4) may follow it.
export function palletsOf(marks, onPallet, palletOf) {
  let on = onPallet || 0, prev = marks[0] ? marks[0].at : null;
  const done = [];
  let cur = { n: 1, from: prev, partial: on > 0, startedAt: on, boxes: [] }, closedLast = false;
  for (const m of marks.slice(1)) {
    if (m.step === 'pallet') {
      const p = done[done.length - 1];
      if (p) p.finishedAt = m.at;
      cur.from = m.at; prev = m.at; closedLast = false;
      continue;
    }
    on++;
    cur.boxes.push({ no: on, secs: (Date.parse(m.at) - Date.parse(prev)) / 1000 });
    prev = m.at; closedLast = false;
    if (palletOf && on >= palletOf) {
      cur.end = m.at; done.push(cur);
      cur = { n: cur.n + 1, from: m.at, partial: false, startedAt: 0, boxes: [] };
      on = 0; closedLast = true;
    }
  }
  return { done, cur, on, closedLast };
}

// Assembly and routines: where a recording stands. Laps are the steps timed so
// far, each less the pauses inside it; the last is still running.
export function lapsOf(marks, now = Date.now()) {
  const pauses = [];
  let open = null;
  for (const m of marks) {
    if (m.step === 'pause') open = { from: Date.parse(m.at), reason: m.reason || null };
    else if (m.step === 'resume' && open) { pauses.push({ ...open, to: Date.parse(m.at) }); open = null; }
  }
  const off = (a, b) => [...pauses, ...(open ? [{ ...open, to: now }] : [])].reduce((s, p) => s + Math.max(0, Math.min(b, p.to) - Math.max(a, p.from)), 0);
  const work = marks.filter(m => m.step === 'start' || m.step === 'next');
  const laps = work.map((m, i) => {
    const a = Date.parse(m.at), b = i + 1 < work.length ? Date.parse(work[i + 1].at) : now;
    return { name: m.name || '', item_id: m.item_id ?? null, round: !!m.round, secs: Math.max(0, (b - a - off(a, b)) / 1000) };
  });
  const start = work[0] ? Date.parse(work[0].at) : null;
  return {
    laps, paused: open, prep: marks.some(m => m.step === 'prep') && !work.length,
    workSecs: start == null ? 0 : Math.max(0, (now - start - off(start, now)) / 1000),
    rounds: work.filter(m => m.round).length + 1,
    unit: (marks.find(m => m.unit) || {}).unit || null,
  };
}
